import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { AlertCircle, Info, Loader2 } from "lucide-react";
import {
  getGoogleDriveStatus,
  isGoogleDriveReconnectRequiredError,
  startGoogleDriveConnect,
} from "@/lib/api/google-drive";
import {
  CONTRACT_PICKER_MIME_TYPES,
  canUseIframePicker,
  filterPickerFiles,
  getGoogleDriveReturnPath,
  GOOGLE_DRIVE_IFRAME_COOKIE_HELP,
  GOOGLE_DRIVE_INLINE_PICKER_HELP,
  GOOGLE_DRIVE_NEW_TAB_HELP,
  isSelectableContractFile,
  KM_PICKER_MIME_TYPES,
  openGoogleDriveOnePick,
  openGooglePicker,
  type PickerSelectedFile,
} from "@/lib/google-picker";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import { useDriveOnePickReturn } from "@/hooks/useDriveOnePickReturn";
import { useGoogleDriveConnectReturn } from "@/hooks/useGoogleDriveConnectReturn";
import { GoogleDriveIcon } from "@/components/icons/GoogleDriveIcon";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

export interface SelectedDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: number | null;
  resourceKey?: string | null;
  accessToken?: string;
}

interface GoogleDrivePickerBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Use KM mime set when importing to a library; defaults to contract review types. */
  importContext?: "contract" | "km";
  /** Called when Google's external picker iframe opens/closes so parent modals can release focus traps. */
  onExternalPickerActiveChange?: (active: boolean) => void;
}

interface GoogleDrivePickerSingleProps extends GoogleDrivePickerBaseProps {
  multiple?: false;
  onSelect: (file: SelectedDriveFile) => void;
  onSelectMany?: never;
}

interface GoogleDrivePickerMultipleProps extends GoogleDrivePickerBaseProps {
  multiple: true;
  onSelect?: never;
  onSelectMany: (files: SelectedDriveFile[]) => void;
}

type GoogleDrivePickerProps = GoogleDrivePickerSingleProps | GoogleDrivePickerMultipleProps;

const COOKIE_HELP_MESSAGE = GOOGLE_DRIVE_IFRAME_COOKIE_HELP;

function mapSelectedFiles(files: PickerSelectedFile[]): SelectedDriveFile[] {
  return files.map((file) => ({
    id: file.id,
    name: file.name,
    mimeType: file.mimeType,
    size: file.size,
    resourceKey: file.resourceKey ?? null,
    accessToken: file.accessToken,
  }));
}

function DriveHeroIcon() {
  return (
    <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/10 bg-slate-800/80">
      <GoogleDriveIcon className="h-8 w-8" />
    </div>
  );
}

function CookieHintCard() {
  return (
    <div className="flex gap-2 rounded-xl border border-white/10 bg-slate-950/40 px-4 py-3 text-left text-xs text-slate-400">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
      <p>{GOOGLE_DRIVE_INLINE_PICKER_HELP}</p>
    </div>
  );
}

function ErrorAlertCard({ message }: { message: string }) {
  return (
    <div className="flex gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-left text-sm text-red-200">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
      <p>{message}</p>
    </div>
  );
}

export function GoogleDrivePicker(props: GoogleDrivePickerProps) {
  const {
    open,
    onOpenChange,
    multiple = false,
    importContext = "contract",
    onExternalPickerActiveChange,
  } = props;
  const onSelect = "onSelect" in props ? props.onSelect : undefined;
  const onSelectMany = "onSelectMany" in props ? props.onSelectMany : undefined;
  const onSelectRef = useRef(onSelect);
  const onSelectManyRef = useRef(onSelectMany);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    onSelectManyRef.current = onSelectMany;
  }, [onSelectMany]);

  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsConnect, setNeedsConnect] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [readyToPick, setReadyToPick] = useState(false);
  const [connectedAccountEmail, setConnectedAccountEmail] = useState<string | null>(null);
  const statusCheckedRef = useRef(false);

  const mimeTypes = importContext === "km" ? KM_PICKER_MIME_TYPES : CONTRACT_PICKER_MIME_TYPES;
  const filterFn = importContext === "km" ? () => true : isSelectableContractFile;

  const resetState = useCallback(() => {
    setLoading(false);
    setCheckingStatus(false);
    setError(null);
    setNeedsConnect(false);
    setNeedsReconnect(false);
    setReadyToPick(false);
    setConnectedAccountEmail(null);
    statusCheckedRef.current = false;
  }, []);

  const checkStatus = useCallback(async () => {
    setCheckingStatus(true);
    setError(null);
    setNeedsConnect(false);
    setNeedsReconnect(false);
    setReadyToPick(false);
    setConnectedAccountEmail(null);

    try {
      const status = await getGoogleDriveStatus();
      if (!status.connected) {
        setNeedsConnect(true);
        setNeedsReconnect(status.reconnectRequired);
        return;
      }

      setConnectedAccountEmail(status.connectedAccountEmail);
      setReadyToPick(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Google Drive status");
    } finally {
      setCheckingStatus(false);
    }
  }, []);

  const setExternalPickerActive = useCallback(
    (active: boolean) => {
      onExternalPickerActiveChange?.(active);
    },
    [onExternalPickerActiveChange]
  );

  const deliverSelectedFiles = useCallback(
    (selected: PickerSelectedFile[]) => {
      const filtered = filterPickerFiles(selected, filterFn);
      if (selected.length > 0 && filtered.length === 0) {
        setError("Selected files are not supported for this import.");
        onOpenChange(true);
        return;
      }

      if (filtered.length === 0) {
        return;
      }

      const mapped = mapSelectedFiles(filtered);
      if (multiple && onSelectManyRef.current) {
        onSelectManyRef.current(mapped);
      } else if (!multiple && onSelectRef.current) {
        onSelectRef.current(mapped[0]!);
      }
    },
    [filterFn, multiple, onOpenChange]
  );

  useDriveOnePickReturn({
    onImport: ({ fileIds, files }) => {
      const selected: PickerSelectedFile[] = files.map((file, index) => ({
        id: fileIds[index] ?? file.name,
        name: file.name,
        mimeType: file.type,
        size: file.size,
      }));
      deliverSelectedFiles(selected);
    },
    onError: (message) => {
      if (message.includes("expired")) {
        setNeedsReconnect(true);
        setNeedsConnect(true);
      }
      setError(message);
      onOpenChange(true);
    },
  });

  useGoogleDriveConnectReturn({
    onConnected: () => {
      onOpenChange(true);
    },
    onError: (message) => {
      setError(message);
      setNeedsConnect(true);
      onOpenChange(true);
    },
  });

  const launchDriveOnePick = useCallback(async () => {
    setLoading(true);
    setError(null);
    onOpenChange(false);

    try {
      await openGoogleDriveOnePick({
        multiple,
        mimeTypes,
        returnPath: getGoogleDriveReturnPath(),
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to open Google Drive in a new tab";
      setError(message.includes("cookie") ? message : `${message}. ${COOKIE_HELP_MESSAGE}`);
      onOpenChange(true);
    } finally {
      setLoading(false);
    }
  }, [mimeTypes, multiple, onOpenChange]);

  const launchDriveIframePicker = useCallback(async () => {
    setLoading(true);
    setError(null);

    onOpenChange(false);

    try {
      const useIframe = await canUseIframePicker();
      if (!useIframe) {
        setError(
          `Embedded picker unavailable. ${GOOGLE_DRIVE_IFRAME_COOKIE_HELP} Use Browse Google Drive to open in a new tab instead.`
        );
        onOpenChange(true);
        return;
      }

      flushSync(() => {
        setExternalPickerActive(true);
      });
      const selected = await openGooglePicker({
        multiple,
        mimeTypes,
      });

      deliverSelectedFiles(selected);
    } catch (err) {
      if (isGoogleDriveReconnectRequiredError(err)) {
        setNeedsReconnect(true);
        setNeedsConnect(true);
        setError("Google Drive authorization has expired. Reconnect to continue.");
      } else {
        const message = err instanceof Error ? err.message : "Failed to open Google Drive picker";
        setError(message.includes("cookie") ? message : `${message}. ${COOKIE_HELP_MESSAGE}`);
      }
      onOpenChange(true);
    } finally {
      setExternalPickerActive(false);
      setLoading(false);
    }
  }, [
    deliverSelectedFiles,
    mimeTypes,
    multiple,
    onOpenChange,
    setExternalPickerActive,
  ]);

  useEffect(() => {
    return () => {
      setExternalPickerActive(false);
    };
  }, [setExternalPickerActive]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    if (statusCheckedRef.current) return;
    statusCheckedRef.current = true;
    void checkStatus();
  }, [checkStatus, open, resetState]);

  async function handleConnect() {
    setError(null);
    try {
      const { url } = await startGoogleDriveConnect(getGoogleDriveReturnPath());
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start Google Drive connection");
    }
  }

  const showSheet = open && (checkingStatus || needsConnect || Boolean(error) || readyToPick);
  const showLoadingBridge = loading && !showSheet;

  return (
    <>
      {showLoadingBridge ? (
        <div className="pointer-events-none fixed inset-0 z-40 grid place-items-center bg-[#05091a]/70 backdrop-blur-sm">
          <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
        </div>
      ) : null}

      <Sheet open={showSheet} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full border-glass-border bg-[#111b35] sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-3">
              <span className="rounded-lg border border-violet-500/30 bg-violet-500/10 p-2">
                <GoogleDriveIcon className="h-5 w-5" />
              </span>
              Google Drive
            </SheetTitle>
            <SheetDescription className={pageDescriptionMobileHiddenClassName}>
              {needsConnect
                ? "Connect your Google account to select files you have access to."
                : "Choose files from your Google Drive."}
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="flex flex-1 flex-col items-center justify-center gap-4 py-8 text-center">
            {checkingStatus ? (
              <>
                <DriveHeroIcon />
                <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
                <p className="text-sm text-slate-400">Checking Google Drive connection…</p>
              </>
            ) : needsConnect ? (
              <>
                <DriveHeroIcon />
                <p className="text-sm text-slate-300">
                  {needsReconnect
                    ? "Your Google Drive authorization has expired."
                    : "Your Google account is not connected yet."}
                </p>
                {error ? <ErrorAlertCard message={error} /> : null}
                <Button variant="accent" onClick={() => void handleConnect()}>
                  {needsReconnect ? "Reconnect Google Drive" : "Connect Google Drive"}
                </Button>
              </>
            ) : readyToPick ? (
              <>
                <DriveHeroIcon />
                <p className="text-sm text-slate-300">
                  Google Drive opens in a new tab with the latest picker UI (recommended).
                </p>
                {connectedAccountEmail ? (
                  <p className="text-xs text-slate-500">Connected as {connectedAccountEmail}</p>
                ) : null}
                <Button variant="accent" onClick={() => void launchDriveOnePick()} disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Opening Drive…
                    </>
                  ) : (
                    "Browse Google Drive"
                  )}
                </Button>
                <p className="max-w-sm text-left text-[11px] leading-relaxed text-slate-500">
                  {GOOGLE_DRIVE_NEW_TAB_HELP}
                </p>
                <button
                  type="button"
                  className="text-xs text-violet-300 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={loading}
                  onClick={() => void launchDriveIframePicker()}
                >
                  Try embedded picker inside the app
                </button>
                <CookieHintCard />
              </>
            ) : error ? (
              <>
                <DriveHeroIcon />
                <ErrorAlertCard message={error} />
              </>
            ) : null}
          </SheetBody>

          <SheetFooter>
            <Button variant="surface" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            {!checkingStatus && !needsConnect && error ? (
              <Button variant="default" onClick={() => void launchDriveOnePick()}>
                Try again
              </Button>
            ) : null}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
